import { notification, Popconfirm, Table } from "antd";
import cuid from "cuid";
import { get, isEqual } from "lodash";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiTrash } from "react-icons/bi";
import { MdAdd } from "react-icons/md";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { useMutation, useQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { normalizeTypologyLabel } from "utils/helpers/leadTypology";

const TRANSLATION_KEY = "MAPPING";

const IGNORED = "IGNORED";

const toRows = (mapping) =>
  Object.entries(mapping || {}).map(([rawLabel, definition]) => ({
    key: cuid(),
    rawLabel,
    typology:
      get(definition, "ignore") === true
        ? IGNORED
        : get(definition, "typologyId", ""),
  }));

const toMapping = (rows) =>
  (rows || []).reduce((mapping, row) => {
    const rawLabel = String(row.rawLabel || "").trim();
    if (!rawLabel || !row.typology) return mapping;

    if (row.typology === IGNORED)
      return { ...mapping, [rawLabel]: { ignore: true } };

    return { ...mapping, [rawLabel]: { typologyId: row.typology } };
  }, {});

const WordingList = ({
  items,
  countLabel,
  renderTranslation,
  renderAction,
}) => (
  <ul>
    {items.map((item) => (
      <li key={item.rawLabel}>
        <span className="lead-typology__wording">{item.rawLabel}</span>
        {renderTranslation && (
          <span className="lead-typology__muted">
            {renderTranslation(item)}
          </span>
        )}
        <span className="lead-typology__muted">{countLabel(item.count)}</span>
        {renderAction && renderAction(item)}
      </li>
    ))}
  </ul>
);

WordingList.propTypes = {
  items: PropTypes.array.isRequired,
  countLabel: PropTypes.func.isRequired,
  renderTranslation: PropTypes.func,
  renderAction: PropTypes.func,
};

const LeadTypologyMappingCard = ({
  sourceId,
  nameSpace,
  value,
  savedValue,
  onChange,
  onAmbiguityChange,
}) => {
  const { t } = useTranslation(nameSpace);
  const COPY = t(TRANSLATION_KEY, { returnObjects: true });

  const [rows, setRows] = useState(() => toRows(value));
  const [focusKey, setFocusKey] = useState(null);

  const { data: typologiesData } = useQuery(graphql.queries.LEAD_TYPOLOGIES, {
    fetchPolicy: "cache-and-network",
  });

  const { data: reviewData, refetch: refetchReview } = useQuery(
    graphql.queries.LEAD_TYPOLOGY_REVIEW,
    {
      variables: { sourceId },
      skip: !sourceId,
      fetchPolicy: "cache-and-network",
    },
  );
  const unmapped = get(reviewData, "leadTypologyReview.unmapped", []) || [];
  const replayable = get(reviewData, "leadTypologyReview.replayable", []) || [];
  const replayableTotal = replayable.reduce(
    (total, { count }) => total + (count || 0),
    0,
  );

  const countMessage = (key, count) =>
    t(`${TRANSLATION_KEY}.${key}`, { count });

  const countLabel = (count) => countMessage("LEAD_COUNT", count);

  const [replayLeadTypologies, { loading: replaying }] = useMutation(
    graphql.mutations.REPLAY_LEAD_TYPOLOGIES,
    {
      onCompleted: (result) => {
        notification.success({
          message: countMessage(
            "REPLAY_DONE",
            get(result, "replayLeadTypologies.updated", 0),
          ),
        });
        refetchReview();
      },
      onError: () => notification.error({ message: COPY.REPLAY_ERROR }),
    },
  );

  const typologyOptions = useMemo(
    () => [
      ...(get(typologiesData, "leadTypologies.data", []) || []).map(
        ({ id, label }) => ({ label, value: id }),
      ),
      { label: COPY.NOT_A_TYPOLOGY, value: IGNORED },
    ],
    [typologiesData, COPY.NOT_A_TYPOLOGY],
  );

  const replaceRows = (nextRows) => {
    setRows(nextRows);
    onChange(toMapping(nextRows));
  };

  const updateRow = (key, changes) =>
    replaceRows(
      rows.map((row) => (row.key === key ? { ...row, ...changes } : row)),
    );

  const addRow = (rawLabel = "") => {
    const key = cuid();
    setFocusKey(key);
    replaceRows([{ key, rawLabel, typology: "" }, ...rows]);
  };

  const removeRow = (key) => replaceRows(rows.filter((row) => row.key !== key));

  const hasUnsavedMapping = !isEqual(toMapping(rows), savedValue || {});

  const incomplete = rows.filter(
    (row) => String(row.rawLabel || "").trim() && !row.typology,
  ).length;

  const ambiguous = (() => {
    const seen = new Set();
    const collisions = new Set();
    rows.forEach((row) => {
      const wording = normalizeTypologyLabel(row.rawLabel);
      if (!wording) return;
      if (seen.has(wording)) collisions.add(wording);
      seen.add(wording);
    });
    return collisions.size;
  })();

  useEffect(() => {
    onAmbiguityChange(ambiguous > 0);
  }, [ambiguous, onAmbiguityChange]);

  const columns = [
    {
      title: COPY.RAW_LABEL,
      dataIndex: "rawLabel",
      render: (rawLabel, row) => (
        <Input
          autoFocus={row.key === focusKey}
          placeholder={COPY.RAW_LABEL_PLACEHOLDER}
          value={rawLabel}
          onChange={(event) =>
            updateRow(row.key, { rawLabel: get(event, "target.value", event) })
          }
        />
      ),
    },
    {
      title: COPY.TYPOLOGY,
      dataIndex: "typology",
      render: (typology, row) => (
        <Select
          placeholder={COPY.TYPOLOGY_PLACEHOLDER}
          options={typologyOptions}
          value={typology || undefined}
          onChange={(next) => updateRow(row.key, { typology: next })}
        />
      ),
    },
    {
      title: COPY.ACTIONS,
      dataIndex: "key",
      width: 80,
      render: (key) => (
        <Button
          type="danger--outlined"
          size="small"
          onClick={() => removeRow(key)}
        >
          <BiTrash size={18} />
        </Button>
      ),
    },
  ];

  return (
    <Card type="simple">
      <div className="lead-typology__header">
        <h3>{COPY.TITLE}</h3>
        <Button type="primary--outlined" onClick={() => addRow()}>
          <MdAdd size={18} /> {COPY.ADD}
        </Button>
      </div>
      <p className="lead-typology__intro">{COPY.INTRO}</p>
      <p className="lead-typology__intro">{COPY.RAW_LABEL_HINT}</p>

      {incomplete > 0 && (
        <p className="lead-typology__warning">{COPY.INCOMPLETE}</p>
      )}

      {ambiguous > 0 && (
        <p className="lead-typology__warning">{COPY.AMBIGUOUS}</p>
      )}

      <Table
        rowKey="key"
        columns={columns}
        dataSource={rows}
        pagination={false}
        locale={{ emptyText: COPY.EMPTY }}
      />

      {unmapped.length > 0 && (
        <div className="lead-typology__section">
          <h4>{COPY.UNMAPPED_TITLE}</h4>
          <p className="lead-typology__intro">{COPY.UNMAPPED_INTRO}</p>
          <WordingList
            items={unmapped}
            countLabel={countLabel}
            renderAction={({ rawLabel }) => (
              <Button
                type="primary--outlined"
                size="small"
                disabled={rows.some(
                  (row) =>
                    normalizeTypologyLabel(row.rawLabel) ===
                    normalizeTypologyLabel(rawLabel),
                )}
                onClick={() => addRow(rawLabel)}
              >
                {COPY.DECLARE}
              </Button>
            )}
          />
        </div>
      )}

      {replayable.length > 0 && (
        <div className="lead-typology__section">
          <h4>{COPY.REPLAY_TITLE}</h4>
          <p className="lead-typology__intro">{COPY.REPLAY_INTRO}</p>
          <WordingList
            items={replayable}
            countLabel={countLabel}
            renderTranslation={({ label, ignored }) =>
              `→ ${ignored === true ? COPY.REPLAY_IGNORED : label}`
            }
          />
          {hasUnsavedMapping && (
            <p className="lead-typology__warning">{COPY.REPLAY_SAVE_FIRST}</p>
          )}
          <Popconfirm
            title={countMessage("REPLAY_CONFIRM", replayableTotal)}
            okText={COPY.REPLAY_OK}
            cancelText={COPY.REPLAY_CANCEL}
            disabled={replaying || hasUnsavedMapping}
            onConfirm={() => replayLeadTypologies({ variables: { sourceId } })}
          >
            <Button type="primary" disabled={replaying || hasUnsavedMapping}>
              {replaying ? COPY.REPLAYING : COPY.REPLAY_ACTION}
            </Button>
          </Popconfirm>
        </div>
      )}
    </Card>
  );
};

LeadTypologyMappingCard.propTypes = {
  sourceId: PropTypes.string,
  nameSpace: PropTypes.string.isRequired,
  value: PropTypes.object,
  savedValue: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onAmbiguityChange: PropTypes.func.isRequired,
};

export default LeadTypologyMappingCard;
